import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Camera, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Btn, Field, Page, Spinner, TopBar } from "@/components/kit";
import DbcPreview from "@/components/DbcPreview";
import { useAuth } from "@/context/auth";
import { api } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { abs, bannerStyle, cardBody, DEFAULT_THEME, FIELDS, PATTERNS, saveTheme, SOCIALS, THEMES, useDbc, useDbcExtra, useSaveDbc } from "@/lib/dbc";
import { isImageFile, prepareImage, toJpeg } from "@/lib/toto";

function PhotoPicker({ card, onChange }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const url = card?.hasPhoto ? `${abs(card.photoUrl)}${card.photoUrl?.includes("?") ? "&" : "?"}v=${card.updatedAt ?? ""}` : null;
  const run = async (fn) => { setBusy(true); try { await fn(); onChange(); } catch (e) { toast.error(e.message); } finally { setBusy(false); } };
  const upload = (f) => run(async () => {
    if (!isImageFile(f)) throw new Error("Please choose an image file.");
    const form = new FormData();
    form.append("file", await toJpeg(await prepareImage(f), 800, 0.85), "photo.jpg");
    await api.upload("/dbc/me/photo", form);
    toast.success("Photo updated.");
  });
  return (
    <div className="flex items-center gap-4" data-testid="dbc-photo">
      <div className="w-20 h-20 rounded-full bg-[#E5E5FD] overflow-hidden flex items-center justify-center">{url ? <img src={url} alt="" className="w-full h-full object-cover" data-testid="dbc-photo-image" /> : <Camera className="w-7 h-7 text-[#0000EC]" />}</div>
      <div className="flex gap-2">
        <Btn variant="outline" className="h-10 text-[14px]" loading={busy} onClick={() => input.current.click()} testid="dbc-photo-upload">{url ? "Change photo" : "Upload photo"}</Btn>
        {url && <Btn variant="ghost" className="h-10 text-[14px] text-[#DC2626]" disabled={busy} onClick={() => run(() => api.del("/dbc/me/photo"))} testid="dbc-photo-remove"><Trash2 className="w-4 h-4" /></Btn>}
      </div>
      <input ref={input} type="file" accept="image/*,.heic,.heif" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(f); }} data-testid="dbc-photo-input" />
    </div>
  );
}

function OtherLinks({ list, onChange }) {
  const set = (i, k, v) => onChange(list.map((o, j) => (j === i ? { ...o, [k]: v } : o)));
  return (
    <div className="space-y-2" data-testid="dbc-other-links">
      {list.map((o, i) => (
        <div key={i} className="flex gap-2">
          <input className="t-input w-1/3" placeholder="Label" value={o.label} onChange={(e) => set(i, "label", e.target.value)} data-testid={`dbc-other-label-${i}`} />
          <input className="t-input flex-1" placeholder="https://" value={o.url} onChange={(e) => set(i, "url", e.target.value)} data-testid={`dbc-other-url-${i}`} />
          <button className="w-12 shrink-0 rounded-xl hover:bg-[#FEF2F2] text-[#DC2626] flex items-center justify-center" onClick={() => onChange(list.filter((_, j) => j !== i))} data-testid={`dbc-other-remove-${i}`}><X className="w-5 h-5" /></button>
        </div>
      ))}
      <Btn variant="ghost" className="h-10 text-[14px] gap-2" onClick={() => onChange([...list, { label: "", url: "" }])} testid="dbc-other-add"><Plus className="w-4 h-4" />Add link</Btn>
    </div>
  );
}

function ThemePicker({ value, onChange }) {
  const custom = !THEMES.some(([c]) => c.toLowerCase() === value.toLowerCase());
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="dbc-theme-picker">
      {THEMES.map(([c, l]) => (
        <button key={c} type="button" title={l} onClick={() => onChange(c)} className={`w-10 h-10 rounded-full ring-offset-2 transition-transform hover:scale-110 ${value.toLowerCase() === c.toLowerCase() ? "ring-2 ring-[#0F172A]" : ""}`} style={{ background: c }} aria-label={l} data-testid={`dbc-theme-${l.toLowerCase().replace(/\s+/g, "-")}`} />
      ))}
      <label className={`relative w-10 h-10 rounded-full cursor-pointer ring-offset-2 overflow-hidden border border-[#E2E8F0] ${custom ? "ring-2 ring-[#0F172A]" : ""}`} title="Custom colour" style={{ background: custom ? value : "conic-gradient(red, yellow, lime, cyan, blue, magenta, red)" }} data-testid="dbc-theme-custom">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} className="absolute inset-0 opacity-0 cursor-pointer" data-testid="dbc-theme-custom-input" />
      </label>
      <span className="text-[13px] text-[#64748B] font-mono" data-testid="dbc-theme-value">{value.toUpperCase()}</span>
    </div>
  );
}

function PatternPicker({ value, color, onChange }) {
  return (
    <div className="grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="dbc-pattern-picker">
      {PATTERNS.map(([k, l]) => (
        <button key={k} type="button" onClick={() => onChange(k)} className={`rounded-[14px] p-1.5 border transition-colors ${value === k ? "border-[#0F172A] ring-1 ring-[#0F172A]" : "border-[#E2E8F0] hover:border-[#94A3B8]"}`} data-testid={`dbc-pattern-${k}`} aria-pressed={value === k}>
          <span className="block h-12 rounded-[10px]" style={bannerStyle(color, k)} />
          <span className="block text-[12px] font-semibold mt-1.5">{l}</span>
        </button>
      ))}
    </div>
  );
}

function Editor({ data, refetch }) {
  const qc = useQueryClient();
  const extra = useDbcExtra(data.exists).data;
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [pattern, setPattern] = useState("plain");
  useEffect(() => { if (extra?.theme) setTheme(extra.theme); }, [extra?.theme]);
  useEffect(() => { if (extra?.pattern) setPattern(extra.pattern); }, [extra?.pattern]);
  const nav = useNavigate();
  const { user } = useAuth();
  const save = useSaveDbc();
  const [f, setF] = useState(() => data.exists ? cardBody(data.card) : { ...cardBody(null), fullName: user?.name ?? "", designation: user?.designation ?? "", company: user?.companyName ?? "", phone: user?.phone ?? "", email: user?.email ?? "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const social = (k, v) => setF({ ...f, socials: { ...f.socials, [k]: v } });
  const submit = async () => {
    if (!f.fullName?.trim()) return toast.error("Please enter your full name.");
    setSaving(true);
    try {
      const other = (f.socials.other ?? []).filter((o) => o.label?.trim() && o.url?.trim());
      await save({ ...f, socials: { ...f.socials, other } });
      if (theme !== (extra?.theme || DEFAULT_THEME) || pattern !== (extra?.pattern || "plain")) await saveTheme(theme, pattern).catch((e) => toast.error(`Card saved, but the colour wasn't: ${e.message}`));
      qc.invalidateQueries({ queryKey: ["dbc-extra"] });
      toast.success("Card saved.");
      nav("/dbc");
    } catch (e) {
      if (e.status === 402) { toast.error("Digital cards are a Premium feature."); return nav("/subscription"); }
      toast.error(e.message);
    } finally { setSaving(false); }
  };
  return (
    <div className="grid gap-10 lg:grid-cols-[1.3fr_1fr] items-start mt-2">
      <div className="space-y-5">
        {data.exists ? <PhotoPicker card={data.card} onChange={refetch} /> : <p className="text-[13px] text-[#64748B]" data-testid="dbc-photo-hint">Save your card first, then add a profile photo.</p>}
        <div className="grid gap-4 sm:grid-cols-2">{FIELDS.map(([k, l]) => <Field key={k} label={l} value={f[k] ?? ""} onChange={set(k)} className={k === "address" ? "sm:col-span-2" : ""} testid={`dbc-${k}`} />)}</div>
        <Field label="About" textarea rows={3} value={f.about ?? ""} onChange={set("about")} maxLength={500} testid="dbc-about" />
        <h2 className="t-section">Card colour</h2>
        <ThemePicker value={theme} onChange={setTheme} />
        <h2 className="t-section">Banner pattern</h2>
        <PatternPicker value={pattern} color={theme} onChange={setPattern} />
        <h2 className="t-section">Social links</h2>
        <div className="grid gap-4 sm:grid-cols-2">{SOCIALS.map(([k, l]) => <Field key={k} label={l} placeholder="https://" value={f.socials?.[k] ?? ""} onChange={(e) => social(k, e.target.value)} testid={`dbc-social-${k}`} />)}</div>
        <h2 className="t-section">Other links</h2>
        <OtherLinks list={f.socials?.other ?? []} onChange={(v) => social("other", v)} />
        <Btn className="w-full sm:w-auto sm:min-w-[240px]" loading={saving} onClick={submit} testid="dbc-save">Save Card</Btn>
      </div>
      <div className="lg:sticky lg:top-36"><p className="t-label mb-2">Preview</p><DbcPreview card={{ ...data.card, ...f }} color={theme} pattern={pattern} testid="dbc-edit-preview" /></div>
    </div>
  );
}

export default function DigitalCardEdit() {
  const q = useDbc();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  if (q.isLoading) return <Page><Spinner /></Page>;
  if (q.error || !q.data?.isPremium) return <Navigate to="/dbc" replace />;
  return <Page><TopBar title={q.data.exists ? "Edit Card" : "Create Card"} backTo="/dbc" align="left" />{<Editor data={q.data} refetch={q.refetch} />}</Page>;
}
